import { isHiddenCrosstabMessage } from '@/lib/crosstab/dani/primer-marks';
import { isHiddenFeasibilityMessage } from '@/lib/feasibility/dani/primer-marks';

import { ROLE_TYPES, STREAMING_EVENT_TYPES } from '../constants';
import type { ChatMessage, ChatMessageContent } from '../types';

/**
 * Phase 0 of DANI memory (docs/prd/M4S-6009-dani-memory.md, A1/A2): how many
 * messages a client sent before DANI's first data answer in a new conversation.
 * Memory is meant to raise the share answered straight after the first message.
 *
 * A conversation is noted when it starts in this tab, with how it began and how
 * many messages it already showed (a dive-deep insight or a duplicate starts
 * with some). Only messages after that point count. A conversation opened from
 * history was not started here, so it never records anything.
 */

/** How a conversation began. Only `typed` starts are read for the metric. */
export type ConversationEntry = 'typed' | 'connect' | 'insight' | 'feasibility' | 'other';

/** Which agent answered. Plan mode's first reply is a plan, so the metric reads `chat`. */
export type AnswerKind = 'chat' | 'planner' | 'reels';

interface ConversationStart {
    entry: ConversationEntry;
    kind: AnswerKind;
    /** Messages already on screen when it started; only later ones count. */
    startIndex: number;
    /** The first data answer's message id, once recorded. */
    firstDataAnswerId?: string;
}

/** Content that makes an answer a data answer. */
const DATA_CONTENT_TYPES: ReadonlySet<string> = new Set([
    STREAMING_EVENT_TYPES.TABLE,
    STREAMING_EVENT_TYPES.CHART,
    STREAMING_EVENT_TYPES.FETCHED_TABLE,
]);

/** Starts kept per tab; the oldest go first past this many. */
const MAX_TRACKED = 50;

const starts = new Map<string, ConversationStart>();

/** How the next new conversation began, when its sender knows (a deep link). */
let nextEntry: ConversationEntry | null = null;

/** The next new conversation's first send did not come from the person typing it. */
export function markNextStartEntry(entry: ConversationEntry): void {
    nextEntry = entry;
}

/** Read and clear `markNextStartEntry`'s value. */
export function claimNextStartEntry(): ConversationEntry | null {
    const entry = nextEntry;
    nextEntry = null;
    return entry;
}

/** Note a conversation that starts in this tab. A second call keeps the first. */
export function noteConversationStart(
    conversationId: string,
    start: Omit<ConversationStart, 'firstDataAnswerId'>,
): void {
    if (!conversationId || starts.has(conversationId)) return;
    starts.set(conversationId, start);
    if (starts.size > MAX_TRACKED) {
        const oldest = starts.keys().next().value;
        if (oldest !== undefined) starts.delete(oldest);
    }
}

function isVisibleUserMessage(message: ChatMessage): boolean {
    return (
        message.role === ROLE_TYPES.USER &&
        !message.silent &&
        !isHiddenFeasibilityMessage(message) &&
        !isHiddenCrosstabMessage(message)
    );
}

function isDataAnswer(message: ChatMessage): boolean {
    return (
        message.role === ROLE_TYPES.ASSISTANT &&
        !message.isStreaming &&
        Array.isArray(message.content) &&
        (message.content as ChatMessageContent[]).some((c) => DATA_CONTENT_TYPES.has(c.type))
    );
}

export interface FirstDataAnswerEvent {
    conversationId: string;
    /** The user's visible messages since the conversation started here. */
    userMessages: number;
    entry: ConversationEntry;
    kind: AnswerKind;
    // Memory is not built yet (phases 1-4); these stay fixed so the event's
    // shape does not change when it is.
    memoryApplied: false;
    memoryOff: false;
    holdout: false;
    setChosenBy: 'none';
    memoryChars: 0;
}

/**
 * The event for this conversation's first finished data answer, the first time
 * one is on screen; null before that, after it was taken, or for a conversation
 * that did not start in this tab.
 */
export function takeFirstDataAnswer(
    conversationId: string | null | undefined,
    messages: ChatMessage[],
): FirstDataAnswerEvent | null {
    const start = conversationId ? starts.get(conversationId) : undefined;
    if (!conversationId || !start || start.firstDataAnswerId) return null;

    let userMessages = 0;
    for (const message of messages.slice(start.startIndex)) {
        if (isVisibleUserMessage(message)) userMessages += 1;
        else if (isDataAnswer(message)) {
            start.firstDataAnswerId = message.id;
            return {
                conversationId,
                userMessages,
                entry: start.entry,
                kind: start.kind,
                memoryApplied: false,
                memoryOff: false,
                holdout: false,
                setChosenBy: 'none',
                memoryChars: 0,
            };
        }
    }
    return null;
}

/** Whether this message is the conversation's recorded first data answer. */
export function isFirstDataAnswer(conversationId: string, messageId: string): boolean {
    return starts.get(conversationId)?.firstDataAnswerId === messageId;
}

/** For tests: forget every start. */
export function resetConversationStarts(): void {
    starts.clear();
    nextEntry = null;
}
